import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Tv,
  Target,
  Zap,
  Users,
  TrendingUp,
  Eye,
  ShieldCheck,
  Sparkles,
  Repeat,
  UserCheck,
  Radar,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  PlayCircle,
  FileText,
  Rocket,
  Film,
  BarChart3,
  Award,
  Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const PDF_URL = "/assets/localbest_tv_ads_pitch_deck.pdf";

// Streaming networks featured in the deck (text badges — no external logo assets required)
const NETWORKS = [
  "Hulu", "Disney+", "ESPN", "Paramount+", "Roku", "Tubi",
  "Pluto TV", "Sling", "HBO Max", "Fubo", "DIRECTV", "Samsung TV",
  "Philo", "Plex", "FOX News", "CBS", "CNN", "NHL", "NFL", "NBA", "MLB",
];

const STATS = [
  { value: "283%", label: "Avg. ROAS on Streaming TV", tone: "#10B981" },
  { value: "12.4M", label: "Total Verified Reach", tone: "#00E5FF" },
  { value: "120M+", label: "Verified IDs (Zero Bots)", tone: "#7C3AED" },
  { value: "57%", label: "Lower CAC on Retargeting", tone: "#F59E0B" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    icon: Crown,
    title: "Claim Your Free Best of Best Listing",
    body: "Publish your listing on LocalBest.ai at no cost. This becomes your growth hub — the page every TV ad, retargeting pixel, and inbound lead points back to.",
  },
  {
    step: "02",
    icon: Film,
    title: "Produce Your Cinematic Commercial",
    body: "Our production team scripts, films, and finalizes a broadcast-ready TV spot for your business. Ready for the biggest screen in the home.",
  },
  {
    step: "03",
    icon: Tv,
    title: "Launch on Premium Streaming TV",
    body: "Air on Hulu, Disney+, Paramount+, Roku, Tubi and more — 100% direct, non-skippable inventory. Targeted person-by-person using our 120M-verified-ID graph, not by guesswork.",
  },
  {
    step: "04",
    icon: Radar,
    title: "Retargeting Pixels Fire on Every Visit",
    body: "The moment a viewer visits your listing page, our tracking pixels tag them. They then see your ads across TV, Meta, Google, YouTube, and TikTok — everywhere they scroll.",
  },
  {
    step: "05",
    icon: DollarSign,
    title: "Co-Op Marketing Subsidizes Your Spend",
    body: "Qualified businesses tap up to $120,000 in co-op marketing support to help offset production and media costs — dramatically lowering your out-of-pocket to get on TV.",
  },
];

const USE_CASES = [
  {
    icon: UserCheck,
    title: "Lookalike",
    body: "Upload your best customers. We find millions more just like them across our 120M identity graph.",
    color: "#00E5FF",
  },
  {
    icon: Target,
    title: "ABM (Account-Based)",
    body: "Target decision-makers at specific companies — on the biggest screen in their home.",
    color: "#7C3AED",
  },
  {
    icon: Repeat,
    title: "Retargeting",
    body: "Bring back listing visitors, cart abandoners, and lapsed leads with premium streaming TV ads.",
    color: "#F59E0B",
  },
  {
    icon: Users,
    title: "Re-Engagement",
    body: "Win back churned customers and dormant contacts with personalized CTV campaigns at scale.",
    color: "#10B981",
  },
];

const WHY_US = [
  { icon: ShieldCheck, title: "Zero Fraud, Zero Bots", body: "Every impression is a real person on a real device. 120M+ verified IDs — no inflated reach." },
  { icon: Tv, title: "100% Direct Premium Inventory", body: "No resellers. Direct access to Disney+, Hulu, Paramount+, Roku, Tubi and more." },
  { icon: BarChart3, title: "Attribution You Can Trust", body: "MTA, MMM, Brand Lift, Incrementality, Foot Traffic — closed-loop reporting on every dollar." },
  { icon: Sparkles, title: "Real-Time AI Optimization", body: "Our platform re-allocates spend to the audiences and creatives that are actually converting for you." },
  { icon: Award, title: "White-Glove Service", body: "A dedicated team helps you launch, optimize, and scale — you're not left figuring TV ads out alone." },
  { icon: Rocket, title: "Non-Skippable Format", body: "8.5× more attentive than social. 47% higher ad recall. The full screen belongs to your brand." },
];

const FUNNEL = [
  { stage: "Top of Funnel", goals: ["Build Awareness", "Attract Traffic"], color: "#00E5FF" },
  { stage: "Mid Funnel", goals: ["Generate Leads", "Increase Sales"], color: "#7C3AED" },
  { stage: "Bottom Funnel", goals: ["Retarget Prospects", "Win-Back"], color: "#F59E0B" },
];

export default function TVAdsPlatformPage() {
  const [showDeck, setShowDeck] = useState(false);

  return (
    <div className="page-container min-h-screen bg-[#020817] text-white" data-testid="tv-ads-platform-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
        {/* Ambient glows */}
        <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-40 pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-40 right-1/4 w-[600px] h-[600px] rounded-full opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(0,229,255,0.30) 0%, transparent 65%)" }} />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-6">
              <DollarSign className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">
                Up to $120,000 in Co-Op Marketing to Subsidize Your TV Ad Spend
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] mb-6 leading-tight">
              Achieve Your Business Goals with{" "}
              <span className="bg-gradient-to-r from-[#7C3AED] via-[#00E5FF] to-[#10B981] bg-clip-text text-transparent">
                TV Ads That Actually Work.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#CBD5E1] max-w-3xl mx-auto mb-4 leading-relaxed">
              LocalBest.ai TV Ads Platform — <span className="text-white font-semibold">Find. Connect. Grow.</span>
              <br className="hidden md:block" />
              Targetable. Measurable. Scalable.
            </p>
            <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto mb-10">
              Get discovered on premium streaming TV, retarget every listing visitor across every screen,
              and lean on up to{" "}
              <span className="text-[#F59E0B] font-semibold">$120,000 in Co-Op Marketing support</span>{" "}
              to help subsidize your spend.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
              <Link to="/submit" data-testid="tv-hero-primary-cta">
                <Button className="bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white rounded-full px-10 py-6 text-lg shadow-[0_0_30px_rgba(124,58,237,0.4)]">
                  Get My Free Listing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <button
                onClick={() => setShowDeck(true)}
                data-testid="tv-hero-view-deck"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white/90 hover:bg-white/5 transition-all text-base"
              >
                <PlayCircle className="w-5 h-5" />
                View the Full Presentation
              </button>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {STATS.map((s) => (
                <div key={s.label}
                     className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm">
                  <div className="text-3xl md:text-4xl font-bold font-['Outfit']" style={{ color: s.tone }}>
                    {s.value}
                  </div>
                  <div className="text-xs md:text-sm text-[#94A3B8] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY THIS MATTERS TO YOU */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-6">
              Search &amp; Social Are{" "}
              <span className="text-[#EF4444]">Slowing Down.</span>
            </h2>
            <p className="text-lg text-[#CBD5E1] max-w-3xl mx-auto">
              In 2025, Search and Social ROAS dropped <span className="text-[#EF4444] font-semibold">-20%</span>.
              Streaming TV ROAS hit <span className="text-[#10B981] font-semibold">+283%</span>. Your next customers
              aren&apos;t scrolling — they&apos;re on the couch.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#EF4444]/10 to-transparent border border-[#EF4444]/20">
              <div className="text-sm uppercase tracking-wide text-[#EF4444] mb-2">The old playbook</div>
              <div className="text-4xl font-bold text-white mb-2">-20% ROAS</div>
              <div className="text-[#94A3B8]">Search &amp; Social, 2025 (Source: Triple Whale)</div>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#10B981]/15 to-transparent border border-[#10B981]/30">
              <div className="text-sm uppercase tracking-wide text-[#10B981] mb-2">The new frontier</div>
              <div className="text-4xl font-bold text-white mb-2">+283% ROAS</div>
              <div className="text-[#94A3B8]">Streaming TV via LocalBest.ai — real customer data</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 bg-white/[0.015] border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-4">
              <Zap className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-sm text-[#7C3AED]">How Your TV Campaign Runs</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-4">
              From Free Listing to <span className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] bg-clip-text text-transparent">Primetime TV</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Five steps. We handle the production, the media buy, and the tracking. You handle the leads that come in.
            </p>
          </motion.div>

          <div className="space-y-4">
            {HOW_IT_WORKS.map((s, idx) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="flex flex-col md:flex-row gap-6 p-6 md:p-8 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#7C3AED]/40 transition-colors"
                data-testid={`tv-step-${s.step}`}
              >
                <div className="flex md:flex-col items-center gap-4 md:min-w-[100px]">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center">
                    <s.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-3xl font-bold font-['Outfit'] text-white/20">{s.step}</div>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold font-['Outfit'] text-white mb-2">{s.title}</h3>
                  <p className="text-[#CBD5E1] leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NETWORKS */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
              Your Ad Runs on the <span className="text-[#00E5FF]">Best Screens in America</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              100% direct, premium streaming inventory. Non-skippable. Brand-safe. Full-screen attention.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3">
            {NETWORKS.map((n) => (
              <span key={n}
                    className="px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-sm font-medium hover:border-[#00E5FF]/40 hover:text-white transition-colors">
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-white/[0.015] border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-4">
              Why <span className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] bg-clip-text text-transparent">LocalBest.ai TV Ads</span> Wins
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Not another CTV vendor. We combine premium supply, verified identity, and closed-loop attribution — with a team that treats your business like their own.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_US.map((w, idx) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.06 }}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#00E5FF]/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mb-4 group-hover:bg-[#7C3AED]/30 transition-colors">
                  <w.icon className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <h3 className="text-lg font-bold font-['Outfit'] text-white mb-2">{w.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-20 md:py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-4">
              Four Ways to <span className="text-[#F59E0B]">Win New Business</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Pick your play. Or run all four. Every campaign is eligible for co-op subsidy support so more of your budget goes to actual reach.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {USE_CASES.map((u, idx) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="p-8 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                       style={{ background: `${u.color}20` }}>
                    <u.icon className="w-7 h-7" style={{ color: u.color }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-['Outfit'] text-white mb-2">{u.title}</h3>
                    <p className="text-[#CBD5E1] leading-relaxed">{u.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNNEL COVERAGE */}
      <section className="py-20 md:py-28 bg-white/[0.015] border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-4">
              One Platform. <span className="bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] bg-clip-text text-transparent">Every Goal.</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Whether you&apos;re driving awareness, generating leads, or closing sales — your funnel is covered end-to-end.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {FUNNEL.map((f, idx) => (
              <motion.div
                key={f.stage}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-2xl bg-white/[0.03] border border-white/10"
                style={{ borderColor: `${f.color}30` }}
              >
                <div className="text-xs uppercase tracking-wider mb-3" style={{ color: f.color }}>{f.stage}</div>
                <ul className="space-y-3">
                  {f.goals.map((g) => (
                    <li key={g} className="flex items-center gap-2 text-white text-lg font-medium">
                      <CheckCircle2 className="w-5 h-5" style={{ color: f.color }} />
                      {g}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RETARGETING NARRATIVE */}
      <section className="py-20 md:py-28 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden p-8 md:p-14 bg-gradient-to-br from-[#7C3AED]/20 via-[#020817] to-[#00E5FF]/10 border border-white/10"
          >
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-30 pointer-events-none"
                 style={{ background: "radial-gradient(circle, rgba(0,229,255,0.3) 0%, transparent 70%)" }} />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 mb-6">
                <Radar className="w-4 h-4 text-[#00E5FF]" />
                <span className="text-sm text-[#00E5FF] font-medium">The Retargeting Loop</span>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] mb-6 leading-tight">
                Someone visits your listing?{" "}
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] bg-clip-text text-transparent">
                  They can&apos;t escape you.
                </span>
              </h2>

              <p className="text-lg text-[#CBD5E1] mb-6 leading-relaxed">
                The instant a shopper lands on your LocalBest.ai listing page, our retargeting pixels
                fire on <span className="text-white font-semibold">TV, Meta, Google, YouTube, and TikTok</span> simultaneously.
              </p>
              <p className="text-lg text-[#CBD5E1] mb-8 leading-relaxed">
                For the next 30 days, your cinematic commercial keeps showing up — on their TV during the game,
                in their Instagram feed at lunch, before their next YouTube video. That&apos;s how a browse turns into a buyer.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Streaming TV", "Meta", "Google", "YouTube", "TikTok", "Roku", "Disney+", "Hulu"].map((p) => (
                  <div key={p} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-center text-white text-sm font-medium">
                    {p}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CO-OP FUND CALLOUT */}
      <section className="py-20 md:py-28 bg-white/[0.015] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-6">
              <DollarSign className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">Co-Op Marketing Fund</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold font-['Outfit'] mb-6">
              Up to <span className="text-[#F59E0B]">$120,000</span> in Co-Op Support.
            </h2>
            <p className="text-lg md:text-xl text-[#CBD5E1] max-w-3xl mx-auto mb-4 leading-relaxed">
              Qualified businesses can tap up to <span className="text-white font-semibold">$120,000 in co-op marketing dollars</span> —
              designed to help offset the cost of your cinematic commercial, your streaming TV media buy, and your retargeting spend across every major platform. It doesn&apos;t remove the invoice; it dramatically shrinks it.
            </p>
            <p className="text-sm text-[#64748B] italic mb-10 max-w-2xl mx-auto">
              Co-Op support subsidizes eligible spend — it is not a free service. Funding is not guaranteed for every applicant. Eligibility, amounts, and match rates vary by business, category, and partnership terms.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/submit" data-testid="tv-coop-primary-cta">
                <Button className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#FBBF24] hover:to-[#F59E0B] text-white rounded-full px-10 py-6 text-lg shadow-[0_0_30px_rgba(245,158,11,0.35)]">
                  Get My Free Listing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <button
                onClick={() => setShowDeck(true)}
                data-testid="tv-coop-view-deck"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white/90 hover:bg-white/5 transition-all text-base"
              >
                <FileText className="w-5 h-5" />
                See the Full Deck
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CASE STUDY SNIPPET */}
      <section className="py-20 md:py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-xs uppercase tracking-widest text-[#7C3AED] mb-3">Real results</div>
              <h3 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4 leading-tight">
                &ldquo;We could ramp spend and see how it affected our blended numbers.
                I feel more confident scaling now.&rdquo;
              </h3>
              <p className="text-[#94A3B8]">— Head of Growth, Leading AI Brand</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-3 gap-3"
            >
              <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#10B981]">20%</div>
                <div className="text-xs text-[#94A3B8] mt-1">Conversion Rate</div>
              </div>
              <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#00E5FF]">&lt;$40</div>
                <div className="text-xs text-[#94A3B8] mt-1">Cost / Install</div>
              </div>
              <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#F59E0B]">+7%</div>
                <div className="text-xs text-[#94A3B8] mt-1">Media Spend</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/10 via-transparent to-[#00E5FF]/10 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <Tv className="w-8 h-8 text-[#00E5FF]" />
              <Sparkles className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <h2 className="text-4xl md:text-6xl font-bold font-['Outfit'] text-white mb-6 leading-tight">
              Big Screen. <span className="bg-gradient-to-r from-[#7C3AED] via-[#00E5FF] to-[#F59E0B] bg-clip-text text-transparent">Big Results.</span>
            </h2>
            <p className="text-lg md:text-xl text-[#CBD5E1] mb-4 max-w-2xl mx-auto">
              Get your free Best of Best listing today — that&apos;s step one. From there we script your commercial, launch your streaming TV campaign, and turn on retargeting, with Co-Op Marketing helping subsidize the cost.
            </p>
            <p className="text-base text-[#94A3B8] mb-10">
              No contracts. No minimums. No sales call required to start.
            </p>

            <Link to="/submit" data-testid="tv-final-cta">
              <Button className="bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white rounded-full px-12 py-7 text-xl shadow-[0_0_40px_rgba(124,58,237,0.5)]">
                Get My Free Listing
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
            </Link>

            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-[#64748B]">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Free to list</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Up to $120K co-op subsidy</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Cinematic commercial production</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Cross-platform retargeting</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DECK MODAL */}
      {showDeck && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowDeck(false)}
          data-testid="tv-deck-modal"
        >
          <div
            className="relative bg-[#0F172A] rounded-2xl w-full max-w-6xl h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-5 h-5 text-[#00E5FF]" />
                <span className="font-semibold">LocalBest.ai TV Ads Platform — Pitch Deck</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#00E5FF] hover:underline px-3 py-1.5"
                  data-testid="tv-deck-open-new-tab"
                >
                  Open in new tab
                </a>
                <button
                  onClick={() => setShowDeck(false)}
                  className="text-white/70 hover:text-white text-xl px-3 py-1"
                  aria-label="Close deck"
                  data-testid="tv-deck-close"
                >
                  ×
                </button>
              </div>
            </div>
            <iframe
              src={PDF_URL}
              title="LocalBest.ai TV Ads Pitch Deck"
              className="w-full h-[calc(90vh-52px)] bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}
